import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import GoogleReviews from './components/GoogleReviews';

export default function HomePage() {
  // Enhanced Schema.org with Organization, LocalBusiness, and WebSite
  const enhancedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": "https://cinewallkopen.nl/#website",
        "url": "https://cinewallkopen.nl/",
        "name": "Cinewallkopen.nl",
        "description": "Luxe cinewalls op maat. Ontwerp, productie en montage.",
        "inLanguage": "nl-NL",
        "publisher": {
          "@id": "https://cinewallkopen.nl/#organization"
        }
      },
      {
        "@type": "Organization",
        "@id": "https://cinewallkopen.nl/#organization",
        "name": "Cinewallkopen.nl",
        "url": "https://cinewallkopen.nl/",
        "email": "info@cinewallkopen.nl",
        "telephone": ["+31624172293", "+31307810110"],
        "logo": {
          "@type": "ImageObject",
          "url": "https://cinewallkopen.nl/LOGO-HIER.png"
        },
        "sameAs": [
          "https://www.instagram.com/INSTAGRAM_HIER",
          "https://www.facebook.com/FACEBOOK_HIER"
        ]
      },
      {
        "@type": "LocalBusiness",
        "@id": "https://cinewallkopen.nl/#localbusiness",
        "name": "Cinewallkopen.nl",
        "url": "https://cinewallkopen.nl/",
        "email": "info@cinewallkopen.nl",
        "telephone": ["+31624172293", "+31307810110"],
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Bramerijweg 15",
          "postalCode": "3555 HX",
          "addressLocality": "Utrecht",
          "addressCountry": "NL"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Netherlands"
        },
        "priceRange": "€€€"
      }
    ]
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Wat kost een cinewall op maat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "De prijs van een cinewall start vanaf circa €2.500 en loopt op afhankelijk van afmetingen, afwerking, opbergruimte en type haard. Via onze configurator ontvangt u snel een eerste indicatie."
        }
      },
      {
        "@type": "Question",
        "name": "Hoe lang duurt het traject?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Na akkoord op ontwerp en planning wordt uw cinewall op maat geproduceerd en professioneel gemonteerd. De totale doorlooptijd verschilt per ontwerp."
        }
      },
      {
        "@type": "Question",
        "name": "Is een cinewall met haard veilig?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, mits technisch correct opgebouwd. Wij houden rekening met ventilatie, materiaalkeuze en afstand tussen haard en tv."
        }
      },
      {
        "@type": "Question",
        "name": "Kan ik eerst een indicatie krijgen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, via onze configurator ontvangt u binnen 1 minuut een eerste prijsindicatie en persoonlijk advies."
        }
      }
    ]
  };

  // SEO Configuration
  useSEO({
    title: 'Cinewall op maat laten maken | Luxe cinewalls met haard & tv',
    description: 'Specialist in luxe cinewalls op maat. Ontwerp, productie en montage van maatwerk cinewalls met tv, haard en LED-verlichting. Vraag gratis offerte aan.',
    keywords: 'cinewall op maat, cinewall kopen, luxe cinewall, cinewall met haard, maatwerk cinewall',
    canonical: 'https://cinewallkopen.nl/',
    schema: enhancedSchema,
  });

  const trustBullets = [
    'Specialist in cinewalls',
    'Volledig maatwerk',
    'Technisch veilige opbouw',
    'Showroombezoek mogelijk',
  ];

  const faqItems = [
    {
      question: 'Wat kost een cinewall op maat?',
      answer: 'De prijs van een cinewall start vanaf circa €2.500 en loopt op afhankelijk van afmetingen, afwerking, opbergruimte en type haard. Via onze configurator ontvangt u snel een eerste indicatie.'
    },
    {
      question: 'Hoe lang duurt het traject?',
      answer: 'Na akkoord op ontwerp en planning wordt uw cinewall op maat geproduceerd en professioneel gemonteerd. De totale doorlooptijd verschilt per ontwerp.'
    },
    {
      question: 'Is een cinewall met haard veilig?',
      answer: 'Ja, mits technisch correct opgebouwd. Wij houden rekening met ventilatie, materiaalkeuze en afstand tussen haard en tv.'
    },
    {
      question: 'Kan ik eerst een indicatie krijgen?',
      answer: 'Ja, via onze configurator ontvangt u binnen 1 minuut een eerste prijsindicatie en persoonlijk advies.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section - Minimal & Premium */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1f7787316154fd9c3dd53f861025ace6.jpg"
            alt="Luxe cinewall met haard"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center"
          >
            <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight">
              Cinewall op maat laten maken
            </h1>
            <p className="text-2xl md:text-3xl text-white/95 mb-12 font-light">
              Luxe cinewalls met haard
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <Link
                to="/cinewall-configurator"
                className="inline-flex items-center gap-3 bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-md font-semibold text-base hover:bg-[#B89858] transition-all duration-300 shadow-lg shadow-[#C7A86A]/20 whitespace-nowrap cursor-pointer"
              >
                Offerte aanvragen in 1 min
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/cinewall-showroom"
                className="inline-flex items-center gap-3 bg-transparent border-2 border-white text-white px-8 py-4 rounded-md font-semibold text-base hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Plan showroomafspraak
                <i className="ri-calendar-line"></i>
              </Link>
            </div>

            <p className="text-white/80 text-sm">
              Binnen 1 minuut een indicatie en persoonlijk advies
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust & Positioning Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#0F0F0F] mb-8">
              Specialist in luxe cinewalls op maat
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-12 max-w-3xl mx-auto">
              Wij ontwerpen en realiseren maatwerk cinewalls waar design, techniek en afwerking samenkomen. 
              Geen standaardoplossingen, maar cinewalls die perfect passen bij uw interieur.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {trustBullets.map((bullet, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C7A86A]/10 rounded-full mb-3">
                    <i className="ri-check-line text-[#C7A86A] text-xl"></i>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{bullet}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Configurator CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#0F0F0F] to-[#1A1A1A] rounded-2xl p-12 lg:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-64 h-64 bg-[#C7A86A] rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#C7A86A] rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10 flex flex-col items-center">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
                Ontvang direct inzicht in uw cinewall
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-2xl leading-relaxed">
                Beantwoord enkele vragen en ontvang direct een prijsindicatie en persoonlijk advies.
              </p>
              
              <Link
                to="/cinewall-configurator"
                className="inline-flex items-center gap-3 bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-md font-semibold text-base hover:bg-[#B89858] transition-all duration-300 shadow-lg shadow-[#C7A86A]/20 whitespace-nowrap cursor-pointer"
              >
                Offerte aanvragen in 1 min
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Inspiration Section */}
      <section className="py-24 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#0F0F0F] mb-4">
              Cinewall inspiratie
            </h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Van modern tot tijdloos. Elke cinewall wordt volledig op maat ontworpen.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 * 0.1 }}
              className="relative group cursor-pointer"
            >
              <div className="w-full h-[400px] overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=ultra%20luxury%20modern%20minimalist%20cinewall%20with%20integrated%20large%20television%20and%20sleek%20electric%20fireplace%20in%20contemporary%20living%20room%20featuring%20clean%20geometric%20lines%20matte%20black%20wall%20panels%20warm%20beige%20accent%20walls%20subtle%20gold%20metallic%20details%20LED%20strip%20lighting%20built-in%20floating%20shelves%20sophisticated%20high-end%20interior%20design%20premium%20materials%20architectural%20lighting%20neutral%20elegant%20color%20palette%20professional%20photography&width=600&height=800&seq=home-insp-modern-minimal-001&orientation=portrait"
                  alt="Moderne minimalistische cinewall"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 1 * 0.1 }}
              className="relative group cursor-pointer"
            >
              <div className="w-full h-[400px] overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=elegant%20luxury%20cinewall%20with%20natural%20stone%20marble%20panels%20integrated%20water%20vapor%20fireplace%20large%20television%20in%20sophisticated%20living%20room%20featuring%20warm%20cream%20beige%20tones%20gold%20brass%20accents%20indirect%20ambient%20lighting%20textured%20stone%20surfaces%20premium%20wood%20details%20high-end%20craftsmanship%20modern%20classic%20interior%20design%20expensive%20materials%20professional%20architectural%20photography&width=600&height=800&seq=home-insp-stone-luxury-002&orientation=portrait"
                  alt="Luxe cinewall met natuursteen"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 2 * 0.1 }}
              className="relative group cursor-pointer"
            >
              <div className="w-full h-[400px] overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=contemporary%20dark%20luxury%20cinewall%20with%20dramatic%20LED%20backlighting%20illuminated%20wall%20niches%20integrated%20television%20and%20fireplace%20in%20modern%20living%20room%20featuring%20charcoal%20grey%20panels%20warm%20white%20LED%20strips%20floating%20display%20shelves%20sophisticated%20ambient%20lighting%20high-contrast%20design%20premium%20materials%20architectural%20interior%20photography%20moody%20elegant%20atmosphere&width=600&height=800&seq=home-insp-dark-led-003&orientation=portrait"
                  alt="Cinewall met LED verlichting"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 3 * 0.1 }}
              className="relative group cursor-pointer"
            >
              <div className="w-full h-[400px] overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20luxury%20cinewall%20with%20premium%20natural%20wood%20panels%20walnut%20oak%20finish%20integrated%20fireplace%20television%20in%20cozy%20elegant%20living%20room%20featuring%20rich%20brown%20wood%20tones%20soft%20beige%20walls%20hidden%20storage%20cabinets%20brass%20metal%20accents%20warm%20ambient%20lighting%20high-end%20carpentry%20craftsmanship%20sophisticated%20interior%20design%20expensive%20materials%20professional%20photography&width=600&height=800&seq=home-insp-wood-warm-004&orientation=portrait"
                  alt="Warme cinewall met houtaccenten"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 4 * 0.1 }}
              className="relative group cursor-pointer"
            >
              <div className="w-full h-[400px] overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=symmetrical%20luxury%20cinewall%20with%20perfect%20proportions%20centered%20television%20fireplace%20below%20balanced%20composition%20in%20elegant%20living%20room%20featuring%20light%20grey%20panels%20white%20walls%20gold%20accents%20organized%20built-in%20storage%20open%20display%20niches%20closed%20cabinets%20harmonious%20design%20ideal%20viewing%20height%20premium%20materials%20sophisticated%20layout%20professional%20interior%20photography&width=600&height=800&seq=home-insp-symmetry-005&orientation=portrait"
                  alt="Symmetrische cinewall met perfecte verhoudingen"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 5 * 0.1 }}
              className="relative group cursor-pointer"
            >
              <div className="w-full h-[400px] overflow-hidden rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=ultra%20modern%20luxury%20cinewall%20with%20realistic%20water%20vapor%20fireplace%20authentic%20flame%20effect%20integrated%20large%20television%20in%20contemporary%20living%20room%20featuring%20sleek%20black%20panels%20cream%20beige%20walls%20sophisticated%20LED%20lighting%20mist%20technology%20high-end%20interior%20design%20elegant%20atmosphere%20premium%20expensive%20materials%20cutting-edge%20technology%20professional%20architectural%20photography&width=600&height=800&seq=home-insp-vapor-tech-006&orientation=portrait"
                  alt="Cinewall met waterdamp haard"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/cinewall-inspiratie"
              className="inline-flex items-center text-[#C7A86A] font-semibold hover:text-[#B89858] transition-colors cursor-pointer"
            >
              Bekijk meer inspiratie
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0F0F0F] mb-6">
              Cinewall kopen – waar moet u op letten?
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Een <strong>cinewall kopen</strong> is meer dan een tv inbouwen. Afmetingen, kijkhoogte, 
              ventilatie en materiaalkeuze bepalen het comfort en de levensduur.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Daarom werken wij uitsluitend met <strong>maatwerk</strong> en technisch doordachte oplossingen. 
              Elke <strong>luxe cinewall</strong> wordt ontworpen met aandacht voor veiligheid, functionaliteit en design.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0F0F0F] mb-4">
              Veelgestelde vragen
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-[#0F0F0F] mb-3">
                  {item.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {item.answer}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/cinewall-faq"
              className="inline-flex items-center text-[#C7A86A] font-semibold hover:text-[#B89858] transition-colors cursor-pointer"
            >
              Bekijk alle veelgestelde vragen
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      <Footer />

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </div>
  );
}
